import React, { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react"
import "./Mosaic.css"
import Card from "./card/Card"
import { useProfile } from "src/shared/hooks/useProfile"
import {
    AlmacenArticulo,
    EstadosAlmacenesArticulos,
    EstadosArticulo,
    TipoArticulo,
    UnidadMedidasArticulo,
    useActualizarProductoMutation,
    useAlmacenesQuery,
} from "src/gql/schema"
import { ModalConfirm } from "src/shared/components/layout"
import BoldText from "src/shared/components/layout/modal-confirm/component-helpers/BoldText/BoldText"
import Icon from "src/shared/icons"
import { useDispatch, useSelector } from "react-redux"
import { RootState } from "src/store/store"
import {
    setIsModalActivateProductFromMosaicOpen,
    setIsModalRefillProductFromMosaicOpen,
    setProductFroMosaicSelected,
} from "src/store/inventario/inventario.slice"
import useMiniSnackbar from "src/shared/hooks/useMiniSnackbar"
import LoaderComponent from "src/shared/components/layout/loader/Loader"
import RefillProduct from "../../modals/RefillProduct/RefillProduct"
import { MosaicCategoryFilter, TODOS_ALMACEN_FILTER } from "../../constants/mosaic-filters"
import useInfiniteScroll from "src/shared/hooks/useInfiniteScroll"
import useArticulosMosaicQuery from "./hooks/useArticulosMosaicQuery"
import { emptyCards } from "src/pages/room-service/productos/Products.constants"
import ProductCard from "src/shared/sections/room-service/Card/ProductCard"
import {v4 as uuid} from 'uuid'
import { FilterSortState } from "../.."
import EmptyState from "src/shared/components/layout/empty-state/EmptyState"

export interface MosaicRef {
    setPage: (v: number) => void
    refetchMosaicArticulos: ({ almacenSelected_, page, filterSortState_ }: {
        almacenSelected_: string;
        page: number;
        filterSortState_?: FilterSortState | undefined;
    }) => Promise<void>
}

const Mosaic = ({
    filterEstadoSelected,
    filterCategoriaSelected,
    onSelectItem,
    searchValue,
    emptyMosaic,
}: {
    filterEstadoSelected: EstadosArticulo | EstadosAlmacenesArticulos | "Todos"
    filterCategoriaSelected: MosaicCategoryFilter
    onSelectItem: (id: string) => void
    searchValue?: AlmacenArticulo | null,
    emptyMosaic: (v: boolean) => void,
}, ref: ForwardedRef<MosaicRef>) => {

    const dispatch = useDispatch()
    const [isLoading, setisLoading] = useState(false)
    const {
        isModalActivateProductFromMosaicOpen,
        productFormMosaicSelected,
        isModalRefillProductFromMosaicOpen,
        // productosMosaic,
    } = useSelector((state: RootState) => state.inventario)
    const { showMiniSnackbar } = useMiniSnackbar()

    const { hotel_id } = useProfile()
    const [updateProduct] = useActualizarProductoMutation()

    const [page, setpage] = useState(1)

    const { data: dataAlmacenes, refetch: reloadAlmacenes } = useAlmacenesQuery({
        variables: {
            hotel_id: hotel_id,
            eliminado: false,
            tipos_articulos: [TipoArticulo.Insumo, TipoArticulo.Proceso, TipoArticulo.Venta]
        },
    })

    const almacenesIds = useMemo(() => dataAlmacenes?.almacenes.map((a) => a.almacen_id), [dataAlmacenes])

    const filterSortState = useMemo(
        () =>
            filterEstadoSelected !== TODOS_ALMACEN_FILTER.label
                ? {
                    disponibilidadFilters: [
                        {
                            filter: filterEstadoSelected,
                            fromHeader: "Disponibilidad",
                            idx: 1,
                        },
                    ],
                }
                : undefined,
        [filterEstadoSelected]
    )

    const {
        articulos,
        currentPage,
        totalPages,
        refecth: refecthArticulos,
        isLoading: isLoadingItems,
    } = useArticulosMosaicQuery({
        page,
        almacenSelected: filterCategoriaSelected,
        almacenes: almacenesIds || [],
        filterSortState,
    })

    useImperativeHandle(ref, () => ({ setPage: (v) => setpage(v), refetchMosaicArticulos: refecthArticulos }))

    const {setContainer, containerRef} = useInfiniteScroll(() => {
        if (currentPage > totalPages || !articulos?.length) {
            return
        }
        setpage((p) => p + 1)
    })

    useEffect(() => {
        containerRef.current?.scrollTo({
            top: 0,
            behavior: "instant" // animado
        })
        setpage(1)
    }, [filterEstadoSelected, filterCategoriaSelected])
    
    useEffect(() => {
        if (!isLoadingItems && !searchValue) {
            emptyMosaic(!articulos?.length)
        }

        if (!isLoadingItems && searchValue === null) {
            emptyMosaic(true)
        }
    }, [articulos, searchValue, isLoadingItems])

    return (
        <>
            <div className="inventory-mosaic__container">
                <div className="inventory-mosaic__main" ref={setContainer}>
                    {searchValue ?                             
                    <Card
                        type={searchValue.articulo?.tipo || TipoArticulo.Insumo}
                        article_id={searchValue?.articulo?.articulo_id || ""}
                        content={searchValue?.articulo?.contenido || 0}
                        estadoAlmacenArticulo={searchValue.estado}
                        estadoArticulo={searchValue.articulo?.estado as EstadosArticulo}
                        isInsumo={searchValue?.articulo?.tipo === TipoArticulo.Insumo}
                        marca={searchValue?.articulo?.marca || ""}
                        measurement={searchValue?.articulo?.unidad || UnidadMedidasArticulo.Pz}
                        mediumLimit={searchValue?.articulo?.cantidad_minima || 5}
                        name={searchValue?.articulo?.nombre || ""}
                        price={searchValue?.precio || 0}
                        units={searchValue?.cantidad || 0}
                        imgUrl={searchValue?.articulo?.foto || undefined}
                        onClickButton={() => {
                            dispatch(setProductFroMosaicSelected(searchValue))
                        }}
                        onSelectItem={(id) => onSelectItem(id)}
                    /> : articulos
                        .map((almacenArticulo) => (
                            <Card
                                type={almacenArticulo.articulo?.tipo || TipoArticulo.Insumo}
                                key={uuid()}
                                article_id={almacenArticulo?.articulo?.articulo_id || ""}
                                content={almacenArticulo?.articulo?.contenido || 0}
                                estadoAlmacenArticulo={almacenArticulo.estado}
                                estadoArticulo={almacenArticulo.articulo?.estado as EstadosArticulo}
                                isInsumo={almacenArticulo?.articulo?.tipo === TipoArticulo.Insumo}
                                marca={almacenArticulo?.articulo?.marca || ""}
                                measurement={almacenArticulo?.articulo?.unidad || UnidadMedidasArticulo.Pz}
                                mediumLimit={almacenArticulo?.articulo?.cantidad_minima || 5}
                                name={almacenArticulo?.articulo?.nombre || ""}
                                price={almacenArticulo?.precio || 0}
                                units={almacenArticulo?.cantidad || 0}
                                imgUrl={almacenArticulo?.articulo?.foto || undefined}
                                onClickButton={() => {
                                    dispatch(setProductFroMosaicSelected(almacenArticulo))
                                }}
                                onSelectItem={(id) => onSelectItem(id)}
                            />
                        ))}
                    {isLoadingItems &&
                        emptyCards.map((_, index) => (
                            <ProductCard
                                conenido=""
                                size={0}
                                className="room-service__card"
                                key={index}
                                load={true}
                                name=""
                                image=""
                                description=""
                                type=""
                                value={0}
                                cost={0}
                                onChange={() => null}
                            />
                        ))}
                </div>
            </div>
            { !isLoadingItems && articulos?.length <= 0 && !searchValue &&
                <EmptyState
                    containerClassName={"inventario__container__empty"}
                    title="Sin artículos encontrados"
                    subtitle="No hay artículos con este estatus en tu almacén."
                    headerIcon="packageFill"
                />
            }
            <ModalConfirm
                isOpen={isModalActivateProductFromMosaicOpen}
                title={"¿Deseas activar este producto?"}
                description={
                    <>
                        Al activar, este producto{" "}
                        <BoldText>estará disponible para resurtir el inventario y para su venta </BoldText>
                        en el servicio a la habitación.
                    </>
                }
                icon={<Icon name="Warning" color="#EB5757" />}
                iconTheme="danger"
                onCloseDialog={({ confirmed }) => {
                    dispatch(setIsModalActivateProductFromMosaicOpen(false))
                    if (confirmed) {
                        setisLoading(true)
                        updateProduct({
                            variables: {
                                articulo_input: {
                                    articulo_id: productFormMosaicSelected?.articulo_id || "",
                                    estado: EstadosArticulo.Activado,
                                    hotel_id
                                },
                            },
                        })
                            .then(() => {
                                reloadAlmacenes()
                                showMiniSnackbar({
                                    text: "Se activó este producto correctamente",
                                    status: "success",
                                    title: "Producto desactivado",
                                })
                            })
                            .catch(() => {
                                showMiniSnackbar({
                                    status: "error",
                                    title: "Error al activar el producto",
                                    text: "¡Ups! Se ha producido un error. Por favor, inténtalo nuevamente.",
                                })
                            })
                            .finally(() => {
                                setisLoading(false)
                            })
                    }
                }}
                confirmLabel="Activar"
            />
            <RefillProduct
                isOpen={isModalRefillProductFromMosaicOpen}
                onConfirm={() => {
                    dispatch(setIsModalRefillProductFromMosaicOpen(false))
                    refecthArticulos({ almacenSelected_: filterCategoriaSelected, page: 1, filterSortState_: filterSortState})
                    containerRef.current?.scrollTo({
                        top: 0,
                        behavior: "smooth" // animado
                    })
                    setpage(1)
                }}
                onClose={() => {
                    dispatch(setIsModalRefillProductFromMosaicOpen(false))
                }}
                product={{
                    id: productFormMosaicSelected?.articulo_id,
                    contenido: productFormMosaicSelected?.articulo?.contenido,
                    almacen_articulo_id: productFormMosaicSelected?.almacen_articulo_id,
                    marca: productFormMosaicSelected?.articulo?.marca || "",
                    nombre: productFormMosaicSelected?.articulo?.nombre,
                    stock: productFormMosaicSelected?.cantidad,
                    unidad: productFormMosaicSelected?.articulo?.unidad,
                    tipo: productFormMosaicSelected?.articulo?.tipo,
                    unidades_disponibles: [],
                }}
            />
            <LoaderComponent visible={isLoading} />
        </>
    )
}

export default forwardRef(Mosaic)
